import { useState, useEffect, useCallback } from 'react';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	CircularProgress,
	Typography,
	FormControlLabel,
	Checkbox,
	FormControl,
	FormGroup,
	FormHelperText,
	Box,
	Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import editIcon from '../../assets/images/icons/editIcon.svg';
import GeneralStatement from './generalStatement';
import Button from '@mui/material/Button';
import { updateQuoteAnswerHandler } from '../../helper/formatQuoteForUpdate';
import { checkQuestionErrors, regexGSOF } from '../../helper/constants';
import { QuestionText } from './questionText';
import { MixpanelWrapper } from '../MixPanelWrapper';
import { QuestionCheckbox } from './questionCheckbox';
import { DecisionTreeOptionAction, QuestionStatus } from '../../helper/questionStatus';
import { declineMessages } from './declineMessages';
import { useQuoteContext } from '../../shared/contexts/QuoteContext';
import {
	DecisionTree,
	DecisionTreeOrQuoteProduct,
	DecisionTreeQuestion,
	QuoteProduct,
} from '../../shared/types/quotes';
import EditIcon from '../icons/EditIcon';
import { colours } from '../../shared/colors';
import { useUpdateQuoteGroupMutation } from '../../services/features/quoteApi/quoteApi';
import { clearError, createError } from '../../services/features/errorSlice/errorSlice';
import { useDispatch } from 'react-redux';
import { clamp } from 'lodash';

const QuestionAccordionHeader = ({ title }: { title: string }) => {
	return (
		<Stack direction='row' alignItems='center'>
			<Box pr={'0.5em'} alignItems='center'>
				<EditIcon
					fill={colours.primaryLight.main}
					style={{ color: colours.primaryLight.main }}
					sx={{ fontSize: '1.25em' }}
				/>
			</Box>
			<Typography variant='subtitle1'>{title}</Typography>
		</Stack>
	);
};

/**
 * Represents a component that displays and manages a series of accordion panels with product questions.
 * @component
 * @param {Object} props - The component's props.
 * @param {string[]} props.decisionTreeErrors - An array of errors associated with product questions.
 * @param {string} props.referralQuestions - An array of referral questions.
 * @param {Function} props.setShowReferMessage - A function to set the display of the referral message.
 * @param {string[]} props.declineQuestions - An array of questions marked as "decline."
 * @param {Function} props.setDeclineQuestions - A function to set the "decline" questions.

 */
const ProductQuestionAccordions = (props: any) => {
	const {
		quoteGroup,
		decisionTree,
		splitDecisionTree,
		setDecisionTree,
		loadingProducts,
		setLoadingProducts,
		isQuoteInProgress,
		isReferralApprovedModifiable,
		quoteCheckbox,
	} = useQuoteContext();

	const readonly = !isQuoteInProgress && !isReferralApprovedModifiable;

	const formik = props.options;

	const dispatch = useDispatch();

	const [updateQuoteGroup] = useUpdateQuoteGroupMutation();

	const [expanded, setExpanded] = useState<string | false | number>(0);
	const [showError, setShowError] = useState(false);
	// const [generalStatementError, setGeneralStatementError] = useState(false);

	const additionalQuestionName =
		DecisionTreeOptionAction[QuestionStatus.AdditionalQuestion].name.toUpperCase();

	// Effect to determine which panel should be open first
	useEffect(() => {
		MixpanelWrapper.track('Create Quote - Product Questions');
		const lastIndex = splitDecisionTree.findIndex((tree) => {
			const currentTab = (
				'decisionTreeQuestions' in tree ? tree : tree?.quoteProductDecisionTree
			) as DecisionTree;
			return currentTab?.decisionTreeQuestions?.some((question) =>
				question.decisionTreeOptions?.every((option) => !option.selected)
			);
		});

		setExpanded(
			splitDecisionTree.length === 0
				? 0
				: lastIndex >= 0
				? lastIndex
				: splitDecisionTree.length - 1
		);
	}, []);

	useEffect(() => {
		props.decisionTreeErrors.forEach((i: number) => handleError(i));
	}, [props.decisionTreeErrors]);

	/**
	 * Handles product question errors and updates the state accordingly.
	 * @function
	 * @param {number} index - The index of the current question.
	 */
	const handleError = async (index: number) => {
		MixpanelWrapper.track('Create Quote - Product Next');

		// console.log('DECISION TREE', decisionTree, 'SPLIT', splitDecisionTree);

		const tabToUpdate = decisionTree[index > decisionTree.length - 1 ? 0 : index];
		const currentItem = splitDecisionTree[index];

		const currentTab: DecisionTree | undefined =
			'decisionTreeQuestions' in currentItem
				? currentItem
				: currentItem?.quoteProductDecisionTree;

		const hasError = currentTab?.decisionTreeQuestions?.some((question) =>
			checkQuestionErrors(question)
		);

		if (hasError) {
			setShowError(true);
			setExpanded(index);
		} else {
			const tabToUpdateDecisionTree = (
				'decisionTreeQuestions' in tabToUpdate
					? tabToUpdate
					: tabToUpdate?.quoteProductDecisionTree
			) as DecisionTree;

			if (!readonly) {
				setLoadingProducts(true);
				await updateAnswerHandler(tabToUpdateDecisionTree);
			}

			setExpanded(clamp(index + 1, 0, splitDecisionTree.length));
			window.scrollTo(0, 0);
			setLoadingProducts(false);
		}
	};

	/**
	 * Updates the answer for the current product question.
	 * @function
	 * @param {any} currentTab - The current product question.
	 */
	const updateAnswerHandler = async (currentTab: DecisionTree) => {
		if (quoteGroup) {
			const update = updateQuoteAnswerHandler(quoteGroup, currentTab);
			await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
				.unwrap()
				.catch((error) => console.log('Error in updating product questions', error));
		}
	};

	/**
	 * Handles a decision action based on the user's choice for a product question.
	 * @function
	 * @param {Object} question - The question data.
	 * @param {string} productName - The name of the product this question belongs to.
	 */
	const handleDecisionAction = (question: DecisionTreeQuestion, productName?: string) => {
		const selection = question.decisionTreeOptions.find((o) => o.selected);
		// console.log('SELECTION', selection);
		if (selection) {
			const decisionAction = selection?.decisionTreeOptionAction;
			// console.log("Decision action:", decisionAction.name);
			// console.log(question);
			switch (decisionAction.name.trim().toUpperCase()) {
				case 'PASS': {
					if (props.referralQuestions.map((q: any) => q.id).includes(question.id)) {
						let referralQuestionsTemp = [...props.referralQuestions].filter(
							(q: any) => q.id !== question.id
						);
						// props.setShowReferMessage(referralQuestionsTemp.length > 0);
						props.setReferralQuestions(referralQuestionsTemp);
					}
					if (
						(props.declineQuestions ?? []).map((q: any) => q.id).includes(question.id)
					) {
						props.setDeclineQuestions([
							...props.declineQuestions.filter((q: any) => q !== question.id),
						]);
					}
					break;
				}
				case 'REFER': {
					let referralQuestionsTemp = [...props.referralQuestions, question];
					// props.setShowReferMessage(true);
					props.setReferralQuestions(referralQuestionsTemp);
					break;
				}
				case 'DECLINE': {
					props.setDeclineQuestions([...(props.declineQuestions ?? []), question]);
					break;
				}
			}
		}
	};

	const updateDecisionTreeQuestions = (
		question: DecisionTreeQuestion,
		questionId: number,
		optionId: number,
		productName: string
	): DecisionTreeQuestion => {
		const newQuestion = {
			...question,
			decisionTreeOptions: question.decisionTreeOptions.map((option) => ({
				...option,
				selected: question.id === questionId ? option.id === optionId : option.selected,
				decisionTreeOptionQuestion: option.decisionTreeOptionQuestion
					? updateDecisionTreeQuestions(
							option.decisionTreeOptionQuestion,
							questionId,
							optionId,
							productName
					  )
					: null,
			})),
		};

		if (question.id === questionId) {
			handleDecisionAction(question, productName);
		}

		return newQuestion;
	};

	const updateDecisionTree = (
		decisionTree: (DecisionTree | QuoteProduct)[],
		itemIndex: number,
		questionId: number,
		optionId: number
	): (DecisionTree | QuoteProduct)[] => {
		return decisionTree.map((item, index) => {
			if (index !== itemIndex) {
				return item;
			}

			let updatedItem = { ...item };
			// console.log('UPDATED ITEM', item, decisionTree);
			let productName = item.name;
			if ('decisionTreeQuestions' in updatedItem) {
				updatedItem.decisionTreeQuestions = updatedItem.decisionTreeQuestions.map(
					(question) =>
						updateDecisionTreeQuestions(question, questionId, optionId, productName)
				);
			} else if (
				'quoteProductDecisionTree' in updatedItem &&
				updatedItem.quoteProductDecisionTree
			) {
				const updatedQuestions =
					updatedItem.quoteProductDecisionTree.decisionTreeQuestions.map((question) =>
						updateDecisionTreeQuestions(question, questionId, optionId, productName)
					);

				return {
					...updatedItem,
					quoteProductDecisionTree: {
						...updatedItem.quoteProductDecisionTree,
						decisionTreeQuestions: updatedQuestions,
					},
				};
			}

			return updatedItem;
		});
	};

	/**
	 * Handles a change in the selected option for a product question.
	 * @function
	 * @param {any} index - The index of the current question.
	 * @param {any} questionId - The ID of the current question.
	 * @param {any} selectedOptionId - The ID of the selected option.
	 */
	const handleControlChange = (index: any, questionId: any, selectedOptionId: any) => {
		let newDecisionTree = updateDecisionTree(
			decisionTree.slice(),
			index,
			questionId,
			selectedOptionId
		);
		setDecisionTree(newDecisionTree);
	};

	/**
	 * Renders additional questions associated with a product question.
	 * @function
	 * @param {any} index - The index of the current question.
	 * @param {any} question - The current question.
	 * @returns {JSX.Element} - The additional questions as JSX.
	 */
	const renderAdditionalQuestions = (index: number, question: DecisionTreeQuestion) => {
		// Find additional questions (ID 10, name Additional Question)
		let additionalQuestion = question.decisionTreeOptions.find(
			(option) =>
				option.selected &&
				option.decisionTreeOptionAction.name.trim().toUpperCase() === additionalQuestionName
		);
		// console.log('ADDITIONAL QUESTION', additionalQuestion);
		if (additionalQuestion?.decisionTreeOptionQuestion) {
			let q = additionalQuestion.decisionTreeOptionQuestion;
			return renderQuestionComponent(q, index, `additional-questions-${question.id}${q.id}`);
		}
	};

	/**
	 * Renders a product question component.
	 * @function
	 * @param {any} question - The current question.
	 * @param {any} index - The index of the current question.
	 * @param {string} key - The unique key for the component.
	 * @returns {JSX.Element} - The question component as JSX.
	 */
	const renderQuestionComponent = (
		question: DecisionTreeQuestion,
		index: number,
		key?: string
	) => {
		const selectedOption = question.decisionTreeOptions.find((o: any) => o.selected);
		const isGSOF = regexGSOF.test(question.questionText);
		const questionsToDisplay = question.decisionTreeOptions.slice();
		questionsToDisplay.sort((a, b) => a.position - b.position);

		return (
			<Box px='1.75em' key={key ?? `question-${index}-id-${question.id}`}>
				<FormControl error={showError && checkQuestionErrors(question)}>
					<FormGroup>
						{isGSOF ? (
							<GeneralStatement />
						) : (
							<Typography variant='subtitle2' style={{ whiteSpace: 'pre-line' }}>
								<QuestionText questionText={question.questionText} />
							</Typography>
						)}

						<Box pb='1em'>
							{questionsToDisplay.map((option) => {
								return (
									<QuestionCheckbox
										key={`option-id-${option.id}-question-id-${question.id}`}
										label={option.name}
										checked={option.selected}
										onChange={() => {
											// console.log('OPTION', option);
											if (!option.selected) {
												handleControlChange(index, question.id, option.id);
											}
										}}
										disabled={loadingProducts || readonly}
									/>
								);
							})}
							{selectedOption?.decisionTreeOptionAction?.name?.toUpperCase() ===
								'DECLINE' && (
								<FormHelperText error sx={{ ml: 0 }}>
									{Object.keys(declineMessages).includes(`${question.id}`)
										? declineMessages[question.id]
										: 'Quote will be declined based on answer to this question.'}
								</FormHelperText>
							)}
							{showError &&
								question.decisionTreeOptions.every((o) => !o.selected) && (
									// !question.decisionTreeOptions[1].selected &&
									// !question.decisionTreeOptions[0].selected && (
									<FormHelperText error={false} sx={{ ml: 0 }}>
										Please select any one option.
									</FormHelperText>
								)}
							{selectedOption?.decisionTreeOptionMessages?.map((message) => (
								<FormHelperText
									error
									sx={{ ml: 0 }}
									key={`option-message-${message.id}`}
								>
									{message.message}
								</FormHelperText>
							))}
						</Box>
					</FormGroup>
				</FormControl>
				{renderAdditionalQuestions(index, question)}
			</Box>
		);
	};

	const renderAccordions = useCallback(() => {
		const checkedDecisionTreeJson = splitDecisionTree.filter(
			(tree: any) =>
				'decisionTreeQuestions' in tree ||
				quoteCheckbox.find((x: any) => x.id === tree.quoteProductDecisionTree.productId) ==
					null
		);

		return checkedDecisionTreeJson.map((tree, i) => {
			// console.log("TREE", tree, "DECISION TREE", checkedDecisionTreeJson);
			const currentTree = (
				'quoteProductDecisionTree' in tree ? tree.quoteProductDecisionTree : tree
			) as DecisionTree;

			const actualIndex = decisionTree.findIndex((t) => t.id === tree.id);

			return (
				<Accordion
					key={`accordion-id-${tree.id}-${i}`}
					disableGutters
					expanded={expanded === i}
					onChange={(_) => setExpanded((prev) => (prev === i ? 'none' : i))}
					elevation={0}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<QuestionAccordionHeader title={tree?.name} />
					</AccordionSummary>
					<AccordionDetails>
						{
							<div key={`businessInfo-${i}`}>
								{currentTree.decisionTreeQuestions.map((question: any) =>
									renderQuestionComponent(question, actualIndex)
								)}
							</div>
						}
						{i !== splitDecisionTree.length - 1 && (
							<Box display='flex' justifyContent='end'>
								<Button
									variant='containedTertiary'
									onClick={() => handleError(i)}
									sx={{
										p: '0.5em 2em',
									}}
								>
									{loadingProducts ? (
										<CircularProgress
											size={30}
											sx={{
												color: 'white',
											}}
										/>
									) : !readonly ? (
										'Save'
									) : (
										'Next'
									)}
								</Button>
							</Box>
						)}
					</AccordionDetails>
				</Accordion>
			);
		});
	}, [decisionTree, splitDecisionTree, expanded, loadingProducts]);

	return <>{renderAccordions()}</>;
};

export default ProductQuestionAccordions;
